import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCompare } from '../context/CompareContext';
import Navbar from '../components/Navbar';
import { CheckCircle, X, ShieldCheck, Loader2 } from 'lucide-react';

const API = '/api';

const ROWS = [
  { label: 'Rent/Month', key: 'rent', format: v => v ? `₹${Number(v).toLocaleString('en-IN')}` : '—' },
  { label: 'Deposit', key: 'deposit', format: v => v ? `₹${Number(v).toLocaleString('en-IN')}` : '—' },
  { label: 'Property Type', key: 'property_type', format: v => v || '—' },
  { label: 'BHK / Size', key: 'bhk_type', format: v => v || '—' },
  { label: 'Category', key: 'property_category', format: v => v || '—' },
  { label: 'Furnishing', key: 'furnishing', format: v => v ? v.charAt(0).toUpperCase() + v.slice(1) : '—' },
  { label: 'City', key: 'city', format: v => v || '—' },
  { label: 'Area/Locality', key: 'area', format: v => v || '—' },
  { label: 'Pincode', key: 'pincode', format: v => v || '—' },
  { label: 'Floor', key: 'floor', format: v => v != null && v !== '' ? v : '—' },
  { label: 'Total Floors', key: 'total_floors', format: v => v != null && v !== '' ? v : '—' },
  { label: 'Property Age', key: 'property_age', format: v => v ? `${v} yrs` : '—' },
  { label: 'Carpet Area', key: 'carpet_area', format: v => v ? `${v} sq.ft` : '—' },
  { label: 'Bachelors Allowed', key: 'bachelor_allowed', format: v => v ? 'Yes' : 'No' },
  { label: 'Pets Allowed', key: 'pets_allowed', format: v => v ? 'Yes' : 'No' },
  { label: 'Maintenance', key: 'maintenance', format: v => v ? `₹${Number(v).toLocaleString('en-IN')}` : '—' },
  { label: 'Available From', key: 'available_from', format: v => v ? new Date(v).toLocaleDateString('en-IN') : '—' },
  { label: 'Listing Plan', key: 'listing_type', format: v => v ? v.charAt(0).toUpperCase() + v.slice(1) : 'Free' },
];

function BestBadge() {
  return <span className="text-[10px] font-bold bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full ml-1.5">Best</span>;
}

export default function ComparePage() {
  const { list, remove, clear } = useCompare();
  const [details, setDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (list.length === 0) { navigate('/search'); return; }
    setLoading(true);
    Promise.all(list.map(p =>
      fetch(`${API}/properties/${p.property_id}`).then(r => r.json())
    )).then(results => {
      const map = {};
      results.forEach(r => { if (r.property_id) map[r.property_id] = r; });
      setDetails(map);
      setLoading(false);
    });
  }, [list, navigate]);

  if (list.length === 0) return null;

  const props = list.map(p => details[p.property_id]).filter(Boolean);
  const minRent = props.length > 0 ? Math.min(...props.map(p => p.rent || Infinity)) : null;

  return (
    <div className="min-h-screen bg-slate-50">
      <Helmet>
        <title>Compare Properties | Rent Mitra</title>
        <meta name="description" content="Compare rental properties side by side on Rent Mitra." />
        <link rel="canonical" href={`${window.location.origin}/compare`} />
      </Helmet>
      <Navbar />

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit,sans-serif' }}>Compare Properties</h1>
            <p className="text-slate-500 text-sm mt-1">Side-by-side comparison of {props.length} properties</p>
          </div>
          <button onClick={() => { clear(); navigate('/search'); }}
            className="text-sm text-slate-500 hover:text-slate-700 flex items-center gap-1.5 border border-slate-200 px-4 py-2 rounded-xl hover:bg-white transition-colors">
            <X className="w-4 h-4" /> Clear & Search
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : (
          <div className="bg-white rounded-3xl border border-slate-200 overflow-x-auto shadow-sm" data-testid="compare-table">
            <table className="w-full" style={{ minWidth: `${list.length * 260 + 180}px` }}>
              {/* Header row */}
              <thead>
                <tr>
                  <th className="text-left px-5 py-4 text-xs font-semibold text-slate-400 uppercase tracking-wider bg-slate-50 w-44 border-r border-slate-100">Attribute</th>
                  {list.map(p => {
                    const d = details[p.property_id];
                    return (
                      <th key={p.property_id} className="px-4 py-4 bg-white border-r border-slate-100 align-top" data-testid={`compare-col-${p.property_id}`}>
                        <div className="relative">
                          <button onClick={() => remove(p.property_id)}
                            className="absolute -top-1 -right-1 w-5 h-5 bg-slate-200 hover:bg-red-500 hover:text-white rounded-full flex items-center justify-center transition-colors">
                            <X className="w-3 h-3" />
                          </button>
                          {d?.images?.[0] ? (
                            <img src={d.images[0]} alt="" className="w-full h-36 object-cover rounded-xl mb-3" />
                          ) : (
                            <div className="w-full h-36 bg-slate-100 rounded-xl mb-3 flex items-center justify-center text-3xl">🏠</div>
                          )}
                          <p className="font-bold text-slate-900 text-sm line-clamp-2 text-left">{d?.title || p.title}</p>
                          {d?.owner_verified && (
                            <div className="flex items-center gap-1 mt-1">
                              <ShieldCheck className="w-3.5 h-3.5 text-blue-600" />
                              <span className="text-[10px] font-semibold text-blue-600">Verified Owner</span>
                            </div>
                          )}
                          <a href={`/property/${p.property_id}`} className="text-xs text-blue-600 hover:underline font-medium mt-1 block text-left">View Details</a>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              {/* Data rows */}
              <tbody>
                {ROWS.map((row, ri) => (
                  <tr key={row.key} className={ri % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                    <td className="px-5 py-3 text-xs font-semibold text-slate-500 border-r border-slate-100 whitespace-nowrap">{row.label}</td>
                    {list.map(p => {
                      const d = details[p.property_id];
                      const val = row.format(d?.[row.key]);
                      const isBest = row.key === 'rent' && d?.rent === minRent && props.length > 1;
                      return (
                        <td key={p.property_id} className={`px-4 py-3 text-sm border-r border-slate-100 ${isBest ? 'text-green-700 font-bold' : 'text-slate-700'}`}>
                          {val}
                          {isBest && <BestBadge />}
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {/* Amenities row */}
                <tr className="bg-white">
                  <td className="px-5 py-3 text-xs font-semibold text-slate-500 border-r border-slate-100">Amenities</td>
                  {list.map(p => {
                    const d = details[p.property_id];
                    const amenities = d?.amenities || [];
                    return (
                      <td key={p.property_id} className="px-4 py-3 border-r border-slate-100">
                        {amenities.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {amenities.map(a => (
                              <span key={a} className="text-[10px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium capitalize">{a}</span>
                            ))}
                          </div>
                        ) : <span className="text-sm text-slate-400">—</span>}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
